import { useUserInfo } from "@/api/user";
import { cn } from "@/utils";
import { PersonAvatar } from "@cohesive/ui-components";
import React from "react";

/** Compact rail-footer trigger: avatar, name, organization. Must forward ref for PopoverTrigger asChild.
 *  `ghostAvatar` renders an empty size-8 box in the avatar slot — the real
 *  avatar is PersistentRailAvatar, pinned over this box from outside the
 *  rail's clip so there is only ever one avatar in the DOM (DS 021). */
export const AccountPopoverRailFooterTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<"button"> & { ghostAvatar?: boolean }
>(function AccountPopoverRailFooterTrigger(
  { className, ghostAvatar, ...props },
  ref,
) {
  const { data: userInfo } = useUserInfo();
  const user = userInfo?.user;
  const orgName = userInfo?.organization?.name ?? "Organization";

  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        "flex min-w-0 flex-1 items-center gap-2.5 rounded-md px-1 py-1 text-left outline-none transition-colors hover:bg-n-05 aria-expanded:bg-n-10 focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-rust focus-visible:outline-offset-[-2px]",
        className,
      )}
      {...props}
    >
      {ghostAvatar ? (
        <span aria-hidden className="size-8 shrink-0" />
      ) : (
        <PersonAvatar
          name={user?.name}
          email={user?.email}
          pictureUrl={user?.picture_url}
        />
      )}
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium text-n-90">
          {user?.name ?? "Account"}
        </span>
        <span className="block truncate text-2xs text-n-50">{orgName}</span>
      </span>
    </button>
  );
});
